import React, { useState, useMemo } from "react";
import { LuChartColumn } from "react-icons/lu";
import { useDashboardTooltip } from "./DashboardTooltip";
import { formatCompact } from "./KpiCards";

const OWNER_GRADIENTS = {
  "Nitin Yadav": "linear-gradient(180deg, #3b82f6, #1d4ed8)",
  "Tushar Singh": "linear-gradient(180deg, #14b8a6, #0d9488)",
  Piyush: "linear-gradient(180deg, #818cf8, #4f46e5)",
  Shubham: "linear-gradient(180deg, #fbbf24, #d97706)",
  "Lavish Mehta": "linear-gradient(180deg, #f472b6, #db2777)",
  "Akash Khetwal": "linear-gradient(180deg, #a78bfa, #7c3aed)",
  "Anand Shrivastav": "linear-gradient(180deg, #94a3b8, #64748b)",
};

const DEFAULT_COLORS = [
  "#2563eb",
  "#0d9488",
  "#6366f1",
  "#f59e0b",
  "#ec4899",
  "#8b5cf6",
  "#64748b",
];

const getShortName = (fullName) => {
  if (!fullName) return "";
  const parts = fullName.trim().split(" ");
  if (parts.length > 1) {
    return `${parts[0]} ${parts[1][0]}.`;
  }
  return parts[0];
};

const formatCurrency = (val) => {
  return `₹${Number(val || 0).toLocaleString("en-IN")}`;
};

export default function OwnerColumnChartCard({
  organizationUsers = [],
  loading = false,
}) {
  const [selectedOwner, setSelectedOwner] = useState("all");
  const { showTooltip, moveTooltip, hideTooltip } = useDashboardTooltip();

  const sortedUsers = useMemo(() => {
    return [...organizationUsers].sort(
      (a, b) => (b.deal_price ?? b.price ?? 0) - (a.deal_price ?? a.price ?? 0)
    );
  }, [organizationUsers]);

  const displayUsers = useMemo(() => {
    if (selectedOwner === "all") return sortedUsers;
    return sortedUsers.filter((u) => u.user === selectedOwner);
  }, [sortedUsers, selectedOwner]);

  const maxVal = useMemo(() => {
    if (sortedUsers.length === 0) return 1;
    return Math.max(...sortedUsers.map((u) => u.deal_price ?? u.price ?? 0), 1);
  }, [sortedUsers]);

  const isSingle = selectedOwner !== "all";

  return (
    <div className="card c5" data-testid="owner-column-chart-card">
      <div className="head">
        <h2>
          <LuChartColumn style={{ color: "#6366f1" }} />
          <span>Deal Value by Owner</span>
        </h2>

        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <select
            id="ownerChartFilter"
            aria-label="Filter chart by owner"
            className="chart-filter-select"
            value={selectedOwner}
            onChange={(e) => setSelectedOwner(e.target.value)}
          >
            <option value="all">All Owners</option>
            {sortedUsers.map((u, i) => (
              <option key={i} value={u.user}>
                {u.user}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading && sortedUsers.length === 0 ? (
        <div className="skeleton-box" style={{ height: "240px", width: "100%" }} />
      ) : displayUsers.length === 0 ? (
        <div className="empty-state-card">No owner data available.</div>
      ) : (
        <div className="col-chart-container" id="ownerChartContainer">
          <div className="col-chart-wrap">
            <div className="col-chart-body">
              <div className="col-gridlines">
                <div className="col-gridline" />
                <div className="col-gridline" />
                <div className="col-gridline" />
              </div>

              <div className={`col-track ${isSingle ? "single-focus" : ""}`} id="colTrack">
                {displayUsers.map((u, i) => {
                  const val = u.deal_price ?? u.price ?? 0;
                  const heightPct = val > 0 ? Math.max(6, (val / maxVal) * 100) : 3;
                  const grad = OWNER_GRADIENTS[u.user] || DEFAULT_COLORS[i % DEFAULT_COLORS.length];
                  const bgStyle =
                    val > 0
                      ? { background: grad }
                      : { background: "var(--border)", border: "1px solid var(--text-soft)" };

                  const tipContent = (
                    <div>
                      <div><b>Owner:</b> {u.user}</div>
                      <div><b>Deal Value:</b> {formatCompact(val)} ({formatCurrency(val)})</div>
                      <div><b>Leads:</b> {u.leads ?? 0}</div>
                      <div><b>Deals:</b> {u.deals ?? 0}</div>
                      <div><b>Points:</b> {Number(u.points ?? 0).toLocaleString("en-US")}</div>
                    </div>
                  );

                  return (
                    <div
                      className="col-item"
                      key={i}
                      data-owner={u.user}
                      onMouseEnter={(e) => showTooltip(e, u.user, tipContent)}
                      onMouseMove={moveTooltip}
                      onMouseLeave={hideTooltip}
                    >
                      <div className="col-pillar-wrap">
                        <div
                          className="col-stat-box"
                          style={{ bottom: `calc(${heightPct}% + 6px)` }}
                        >
                          <div className="col-val-text">{formatCompact(val)}</div>
                          <div className="col-leads-badge">{u.leads ?? 0} Leads</div>
                        </div>

                        <div
                          className="col-pillar"
                          style={{
                            height: `${heightPct}%`,
                            ...bgStyle,
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className={`col-xaxis ${isSingle ? "single-focus" : ""}`}>
              {displayUsers.map((u, i) => (
                <div className="col-xaxis-name" key={i} title={u.user}>
                  {u.user}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
